import { useEffect } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { Switch, Route } from "wouter";
import { LoadingPanel } from "@/components/LoadingPanel";
import { ErrorPanel } from "@/components/ErrorPanel";
import { SignedInView } from "@/components/SignedInView";
import { DebugView } from "@/components/DebugView";
import { LogoutReturnView } from "@/components/LogoutReturnView";
import type { OidcRuntimeConfig } from "@/auth/config";
import type { AuthError } from "@/types/auth";

export type AppProps = {
  config: OidcRuntimeConfig;
};

function activeNavigatorMessage(activeNavigator: string | undefined): string {
  switch (activeNavigator) {
    case "signinRedirect":
      return "Redirecting to sign in";
    case "signinSilent":
      return "Refreshing the session";
    case "signoutRedirect":
      return "Signing out";
    default:
      return "Checking the session";
  }
}

export function App({ config }: AppProps) {
  const auth = useAuth();
  const hasAuthParamsInUrl = hasAuthParams();

  useEffect(() => {
    if (
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !hasAuthParamsInUrl &&
      !auth.activeNavigator &&
      !auth.error &&
      window.location.pathname !== "/debug"
    ) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("proxy") === "true") {
        params.delete("proxy");
        const search = params.toString();
        window.history.replaceState({}, document.title, window.location.pathname + (search ? `?${search}` : ""));
      }
      void auth.signinRedirect();
    }
  }, [auth, hasAuthParamsInUrl]);

  return (
    <Switch>
      <Route path="/logout/callback">
        <LogoutReturnView />
      </Route>
      <Route path="/debug">
        <DebugView config={config} />
      </Route>
      <Route>
        {() => {
          if (auth.error) {
            return <ErrorPanel error={auth.error as AuthError} />;
          }

          if (auth.activeNavigator || auth.isLoading || hasAuthParamsInUrl) {
            return <LoadingPanel message={activeNavigatorMessage(auth.activeNavigator)} />;
          }

          if (!auth.isAuthenticated) {
            return <LoadingPanel message="Redirecting to sign in" />;
          }

          return <SignedInView config={config} />;
        }}
      </Route>
    </Switch>
  );
}
